import {
    type CollectionDossierFormalite,
    type SousClientApi,
    type PrescripteurApi,
} from '@europrocurement/l2d-domain';
import { type FproDossierCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { type AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { BillingInfo } from '@europrocurement/l2d-modules/modules/Dossiers';
import { castIriToId } from '@europrocurement/l2d-utils';
import { Box, CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

type RecapBillingBodyProps = {
    prescripteur: PrescripteurApi;
    premierDossier: FproDossierCollectiondossierRead;
    sousClients: SousClientApi[];
    sousClient?: SousClientApi | undefined;
    collection: CollectionDossierFormalite;
    refInterne: string | null;
    commentaires: string | null;
    dossierUrgent: boolean;
    onChange?: (
        collection: CollectionDossierFormalite,
        refInterne: string | null,
        commentaires: string | null,
        dossierUrgent: boolean | null,
        indirectSale: boolean,
        recipientId: number,
    ) => void;
};
const AdresseComposee = (
    adresse: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
): string => {
    const items = [
        adresse?.adresse1,
        adresse?.adresse2,
        adresse?.adresse3,
        adresse?.adresse4,
        adresse?.codePostal,
        adresse?.ville,
    ];

    return items.filter((item) => item !== undefined).join(' ');
};

const RecapBillingBody: React.FunctionComponent<RecapBillingBodyProps> = function ({
    prescripteur,
    premierDossier,
    sousClients,
    sousClient = prescripteur,
    collection,
    refInterne,
    commentaires,
    dossierUrgent,
    onChange,
}) {
    const clientId = useMemo(
        () => castIriToId(premierDossier.client as unknown as string) as number,
        [premierDossier.client],
    );
    const [recipientId, setRecipientId] = useState<number>(clientId);
    const [indirectSale, setIndirectSale] = useState<boolean>(
        collection.venteIndirecte ? collection.venteIndirecte : false,
    );
    const clientAddresses = useMemo(
        () =>
            sousClients.map((client) => ({
                clientId: client.id as number,
                label: client.raisonSociale,
                siren: client.siren as string,
                address: AdresseComposee(client.adresses[0]),
            })),
        [sousClients],
    );

    const hasClientAddress = useMemo(
        () =>
            prescripteur.id === clientId ||
            sousClients.find((client) => client.id === clientId) !== undefined,
        [clientId, prescripteur.id, sousClients],
    );

    if (!hasClientAddress) {
        clientAddresses?.push({
            clientId,
            label: sousClient.raisonSociale,
            siren: sousClient.siren ? sousClient.siren : '',
            address: AdresseComposee(sousClient.adresses[0]),
        });
    }

    const onChangeHandler = useCallback(() => {
        if (onChange) {
            onChange(
                collection,
                refInterne,
                commentaires,
                dossierUrgent,
                indirectSale,
                recipientId,
            );
        }
    }, [commentaires, collection, dossierUrgent, onChange, refInterne, indirectSale, recipientId]);

    useEffect(() => {
        onChangeHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossierUrgent, refInterne, indirectSale, recipientId, commentaires]);

    const clientOpereId = useMemo(
        () => castIriToId(premierDossier.clientOpere as unknown as string) as number,
        [premierDossier.clientOpere],
    );

    useEffect(() => {
        let newIndirectSale: boolean = indirectSale;

        if (prescripteur.id === recipientId) {
            newIndirectSale = false;
        } else {
            const sousClientCollection = sousClients?.find((t) => t.id === recipientId);

            if (sousClientCollection) {
                newIndirectSale = sousClientCollection.venteIndirecte
                    ? sousClientCollection.venteIndirecte
                    : false;
            }
        }

        const newClientId = recipientId || clientId;
        const hasChanges = newIndirectSale !== indirectSale || newClientId !== clientId;

        if (!hasChanges) {
            return;
        }

        if (newIndirectSale !== indirectSale) {
            setIndirectSale(newIndirectSale);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientId]);

    const handleRecipientChange = (newRecipientId?: number) => {
        setRecipientId(
            newRecipientId ||
                (prescripteur.id !== clientOpereId ? clientOpereId : (sousClients[0].id as number)),
        );
    };

    const handleIndirectSaleChange = (newIndirectSale: boolean) => {
        setIndirectSale(newIndirectSale);
    };

    const mainAddressePrescripteur = useMemo(
        () => prescripteur.adresses.find((address) => address.defaut),
        [prescripteur.adresses],
    );

    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {prescripteur.id && mainAddressePrescripteur ? (
                <BillingInfo
                    prescriberId={prescripteur.id}
                    recipientId={recipientId}
                    prescriberLabel={prescripteur.raisonSociale as string}
                    prescriberSiren={prescripteur.siren as string}
                    prescriberAddress={AdresseComposee(mainAddressePrescripteur)}
                    onRecipientChange={handleRecipientChange}
                    addresses={clientAddresses}
                    billingType={indirectSale}
                    onBillingTypeChange={handleIndirectSaleChange}
                    canEdit
                    canEditBillingType
                />
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                    <CircularProgress />
                </Box>
            )}
        </>
    );
};

export default RecapBillingBody;
